/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Component, JSXElement, For, Show, onMount, onCleanup } from 'solid-js';

/**
 * SolidJS Rightpanel component - Simplified Design
 *
 * Usage Example:
 * ```tsx
 * <Rightpanel
 *   header={
 *     <div>
 *       <h3>My Panel Title</h3>
 *       <button>Action Button</button>
 *     </div>
 *   }
 *   content={<div>Panel content goes here</div>}
 *   footer={{
 *     children: <div>Footer content</div>
 *   }}
 * />
 * ```
 *
 * Note: This component uses `header`, `content`, and `footer` props
 * for JSX content - the host application has full control over the layout.
 */

export interface IRightpanelFooterButton {
  text: string;
  class: string;
  props: any;
}

export interface IRightpanelFooter {
  button?: IRightpanelFooterButton[];
  children?: JSXElement;
}

export interface RightpanelProps {
  header?: JSXElement; // Simple header content like section
  // Header navigation props
  headerTitle?: string;
  topLeftButton?: string;
  topRightPrimaryButton?: string;
  topRightSecondaryButton?: string;
  footer?: IRightpanelFooter;
  sectionProps?: any;
  bodyProps?: any;
  content?: JSXElement;
  // Callback props for event handling
  onFooterButtonClick?(
    button: IRightpanelFooterButton,
    event: MouseEvent
  ): void;
  onLeftButtonClick?(): void;
  onPrimaryButtonClick?(): void;
  onSecondaryButtonClick?(): void;
}

const Rightpanel: Component<RightpanelProps> = (props) => {
  let panelRef: HTMLDivElement | undefined;
  let sectionRef: HTMLElement | undefined;

  // Publish the scroller's scrollbar width as a panel-scoped CSS var so
  // overlays slotted into .panel-body can compensate for the width a classic
  // scrollbar removes from the content box.
  onMount(() => {
    if (!panelRef || !sectionRef) return;
    const panel = panelRef;
    const section = sectionRef;
    const publish = (): void => {
      panel.style.setProperty(
        '--scrollbar-width',
        `${section.offsetWidth - section.clientWidth}px`
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(section);
    onCleanup(() => observer.disconnect());
  });

  const handleFooterButtonClick = (
    button: IRightpanelFooterButton,
    event: MouseEvent
  ) => {
    // Execute original button props onClick if exists
    if (button.props?.onClick) {
      button.props.onClick(event);
    }

    // Call callback prop if provided
    if (props.onFooterButtonClick) {
      props.onFooterButtonClick(button, event);
    }
  };

  // Helper functions
  const getClassNames = (
    ...classes: (string | undefined | boolean)[]
  ): string => {
    return classes.filter(Boolean).join(' ');
  };

  const getSectionProps = () => {
    const sectionProps = props.sectionProps || {};
    if (!props.footer) {
      return {
        class: getClassNames('mb-0', sectionProps.className),
        ...sectionProps,
      };
    }
    return sectionProps;
  };

  const getBodyProps = () => {
    return props.bodyProps || {};
  };

  // Render footer buttons
  const renderFooterButtons = () => {
    const footer = props.footer;

    if (!footer || !footer.button) {
      return null;
    }

    const buttons = footer.button;

    if (buttons.length === 1) {
      const button = buttons[0];

      return (
        <div class="full-width">
          <button
            class={getClassNames(button.class, 'button')}
            {...button.props}
            onClick={(e) => {
              handleFooterButtonClick(button, e);
            }}
          >
            {button.text}
          </button>
        </div>
      );
    }

    return (
      <For each={buttons}>
        {(button) => {
          const buttonClass = ['button', button.class];
          if (buttons.length < 2) {
            buttonClass.push('full-width', 'mx-0');
          }

          return (
            <button
              class={buttonClass.join(' ')}
              {...button.props}
              onClick={(e) => {
                handleFooterButtonClick(button, e);
              }}
            >
              {button.text}
            </button>
          );
        }}
      </For>
    );
  };

  // Render header navigation
  const renderHeader = () => {
    if (props.header) {
      return <header>{props.header}</header>;
    }

    if (
      props.headerTitle ||
      props.topLeftButton ||
      props.topRightPrimaryButton ||
      props.topRightSecondaryButton
    ) {
      return (
        <header>
          <Show when={props.headerTitle}>
            <div class="panel-title">
              <h3 class="header-title">{props.headerTitle}</h3>
            </div>
          </Show>
          <Show when={props.topLeftButton}>
            <button
              class="button icon-medium icon-circle tertiary"
              onClick={() => props.onLeftButtonClick?.()}
            >
              <i class={`icon-font ${props.topLeftButton ?? ''}`} />
            </button>
          </Show>
          <Show when={props.topRightPrimaryButton}>
            <button
              class="button icon-medium icon-circle primary"
              onClick={() => props.onPrimaryButtonClick?.()}
            >
              <i class={`icon-font ${props.topRightPrimaryButton ?? ''}`} />
            </button>
          </Show>
          <Show when={props.topRightSecondaryButton}>
            <button
              class="button icon-medium icon-circle tertiary"
              onClick={() => props.onSecondaryButtonClick?.()}
            >
              <i class={`icon-font ${props.topRightSecondaryButton ?? ''}`} />
            </button>
          </Show>
        </header>
      );
    }

    return null;
  };

  return (
    <div
      ref={panelRef}
      class="panel full-width mw-100"
      style={{ 'max-width': '20rem' }}
    >
      {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
      {renderHeader()}

      <section {...getSectionProps()} ref={sectionRef}>
        <div class="panel-body" {...getBodyProps()}>
          {props.content}
        </div>
      </section>

      <Show when={props.footer}>
        <footer class={props.footer?.children ? 'p-0' : ''}>
          {props.footer?.children}
          <Show when={props.footer?.button}>
            <div class="wrap-buttons px-2 py-2 mx-0">
              {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
              {renderFooterButtons()}
            </div>
          </Show>
        </footer>
      </Show>
    </div>
  );
};

export default Rightpanel;
